import { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ZmanimData } from '../types';
import { computeZmanimForDate } from '../utils/hebcalZmanim';
import { normalizeZmanim } from '../utils/zmanimRules';
import { getTodayString } from '../utils/dateShortcuts';

export function useZmanim(date?: string) {
  const [zmanim, setZmanim] = useState<ZmanimData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const targetDate = date || getTodayString();

  useEffect(() => {
    const fetchZmanim = async () => {
      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, 'zmanimCache', targetDate);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setZmanim(normalizeZmanim(docSnap.data() as ZmanimData));
        } else {
          setZmanim(computeZmanimForDate(targetDate));
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchZmanim();
  }, [targetDate]);

  return { zmanim, loading, error };
}

/** Zmanim for several dates (cache first, then Hebcal), e.g. a whole Shabbos / Yom Tov stretch. */
export function useZmanimForDates(dates: string[]) {
  const [byDate, setByDate] = useState<Record<string, ZmanimData>>({});
  const key = dates.join(',');

  useEffect(() => {
    let cancelled = false;
    const list = key ? key.split(',') : [];
    Promise.all(
      list.map(async (d) => {
        try {
          const snap = await getDoc(doc(db, 'zmanimCache', d));
          return [d, snap.exists() ? normalizeZmanim(snap.data() as ZmanimData) : computeZmanimForDate(d)] as const;
        } catch {
          return [d, computeZmanimForDate(d)] as const;
        }
      })
    ).then((pairs) => {
      if (!cancelled) setByDate(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return byDate;
}
